import { BadInputError } from '@watershed/errors/BadInputError';
import { ASTCall, ASTExpr, ASTKwarg } from '@watershed/lscript/ast';
import { extractStringLiteral } from '../literal';
import {
  assertArgShape,
  assertKwargShape,
  assertType,
  TypecheckWarning,
} from '../typecheck';
import { Context, Macro, SqlExpression } from '../types';
import { getCurrencyUnit, Unit } from '@watershed/lscript/unit';
import { l } from '../template';
import { macro } from './helpers/macros';
import { traceHelper } from '../sql/internal';
/* eslint-disable @watershed/no-dangerous-raw-sql -- DuckDB usage only */
import { dangerousRawSql, sqlF } from '@watershed/lscript/sqlTemplate';
import { TraceASTConversion, TraceASTType } from '../trace';
import { exprToSql } from '../sql';
import { UnexpectedError } from '@watershed/errors/UnexpectedError';
import { dateCastIfNotAlreadyCast } from '../sql/date_cast';

export const FOREX_REFERENCE_DATA_SLUG = 'forex:v2';

// Must match baseCurrency from "forex" ref data
const BASE_CURRENCY = 'USD';

function getExactKwarg(
  call: ReadonlyMap<string, ASTKwarg>,
  name: string,
  fallback?: ASTExpr
): ASTExpr {
  const kwargEntry = call.get(name);
  if (!kwargEntry) {
    if (fallback) {
      return fallback;
    } else {
      throw new BadInputError(`forex function: ${name} is required`);
    }
  } else if (kwargEntry.t !== 'exact') {
    throw new BadInputError(`forex function: ${name} must be an exact match`);
  }
  return kwargEntry.expr;
}

export const forex: Macro = macro({
  expand(call: ASTCall): ASTExpr {
    const amountExpr = call.args[0];
    const monthExpr = getExactKwarg(call.kwargs, 'month');
    const fromExpr = getExactKwarg(call.kwargs, 'from');
    const toExpr = getExactKwarg(call.kwargs, 'to', {
      t: 'string',
      v: BASE_CURRENCY,
    });
    const toCurrency = extractStringLiteral(toExpr);

    // if you put quotes around FOREX_REFERENCE_DATA_SLUG, this will break
    const fromRefExpr = l`ref(${FOREX_REFERENCE_DATA_SLUG}, "value", month=${monthExpr}, currency=${fromExpr})`;
    const toRefExpr = l`ref(${FOREX_REFERENCE_DATA_SLUG}, "value", month=${monthExpr}, currency=${toCurrency})`;
    // In theory we can elide at least one of these reference data lookups
    // (e.g., if the toCurrency is the BASE_CURRENCY), but for backwards
    // compatibility with old evaluations the forex(...) function needs to emit
    // two reference data lookups, and they have to be in this specific order.
    return l`${amountExpr} * ${toRefExpr} / ${fromRefExpr}`;
  },
  typecheck(ctx: Context, call: ASTCall, warnings?: Array<TypecheckWarning>) {
    assertArgShape(call, 1, 1);
    assertKwargShape(call, ['from', 'month'], false, ['to'], undefined);

    const amountExpr = call.args[0];
    const monthExpr = getExactKwarg(call.kwargs, 'month');
    const fromExpr = getExactKwarg(call.kwargs, 'from');
    const toExpr = getExactKwarg(call.kwargs, 'to', {
      t: 'string',
      v: BASE_CURRENCY,
    });

    // We could, at some point, accept a quantity value for "amount" and use that to infer
    // "from" but since this value is likely to be coming from bart, which doesn't have
    // a concept of quantity, it's probably not worth it (yet).

    assertType(ctx, { ty: 'date' }, monthExpr, warnings);
    assertType(ctx, { ty: 'string' }, fromExpr, warnings);
    assertType(ctx, { ty: 'string' }, toExpr, warnings);
    assertType(ctx, { ty: 'number' }, amountExpr, warnings);

    // The to value needs to be a fixed literal for this to make sense.
    // This will raise if the currency doesn't exist
    //
    // NOTE(carl): a previous version of this code allowed for the `to=` param
    // to have a variable value. There are a few old models (EMEs between late
    // March 2023 and mid May 2023) that erroneously require this behavior. It's
    // possible to *legitimately* require this behavior: for instance, a
    // database of EFs might be denominated in the local currency of each
    // country. If that case arises, we'll need to change this code to return a
    // currency Quantity type.
    const toCurrency = extractStringLiteral(toExpr);
    const toUnit = assertCurrencyUnit(toCurrency);
    return { ty: 'number', unit: toUnit };
  },
  customTraceHook(ctx, call, eSql): SqlExpression {
    const amountExpr = call.args[0];
    const monthExpr = getExactKwarg(call.kwargs, 'month');
    const fromExpr = getExactKwarg(call.kwargs, 'from');
    const toExpr = getExactKwarg(call.kwargs, 'to', {
      t: 'string',
      v: BASE_CURRENCY,
    });
    const toCurrency = extractStringLiteral(toExpr);
    const toUnit = assertCurrencyUnit(toCurrency);

    // if you put quotes around FOREX_REFERENCE_DATA_SLUG, this will break
    const fromRefExpr = l`ref(${FOREX_REFERENCE_DATA_SLUG}, "value", month=${monthExpr}, currency=${fromExpr})`;
    const toRefExpr = l`ref(${FOREX_REFERENCE_DATA_SLUG}, "value", month=${monthExpr}, currency=${toCurrency})`;
    const rateExpr = l`${toRefExpr} / ${fromRefExpr}`;

    const amountSql = exprToSql(ctx, amountExpr);
    // TODO: ideally we know which part of the world is responsible for casting
    // these dates, and do it in that one place only
    const monthSql = dateCastIfNotAlreadyCast(
      dangerousRawSql(exprToSql(ctx, monthExpr).expr)
    );
    const fromSql = dangerousRawSql(exprToSql(ctx, fromExpr).expr);
    const rateSql = dangerousRawSql(exprToSql(ctx, rateExpr).expr);

    const conversion = traceHelper<TraceASTConversion>(
      { ty: 'number', unit: toUnit },
      eSql,
      (sub) => ({
        t: TraceASTType.TConversion,
        d: sqlF`'[${toCurrency}] = '||${rateSql}||' * ['||${fromSql}||'] (month='||strftime(${monthSql}, '%Y-%m-%d')||')'`,
        e: sub(amountSql.trace),
      })
    );
    UnexpectedError.invariant(
      amountSql.trace !== null,
      'forex(...) acts on numbers, which must have traces'
    );
    UnexpectedError.invariant(
      conversion.trace !== null,
      'forex(...) acts on numbers, which must have traces'
    );

    return {
      expr: eSql.expr,
      stage: eSql.stage,
      unitExpr: eSql.unitExpr,
      error: eSql.error,
      trace: {
        expr: sqlF`CASE WHEN ${fromSql} = ${toCurrency} THEN ${amountSql.trace.expr} ELSE ${conversion.trace.expr} END`,
        fingerprintExpr: sqlF`CASE WHEN ${fromSql} = ${toCurrency} THEN ${amountSql.trace.fingerprintExpr} ELSE ${conversion.trace.fingerprintExpr} END`,
      },
    };
  },
});

function assertCurrencyUnit(toCurrency: string): Unit {
  const toUnit = getCurrencyUnit(toCurrency);
  BadInputError.invariant(toUnit, 'Unknown currency: ' + toCurrency);
  return toUnit;
}
